import React, { useState } from 'react';
import {
  Banner,
  ButtonGroup,
  Drawer,
  ErrorHandler,
  LoadingState,
  PrimaryButton,
  SecondaryButton,
  Text,
} from '@common/components';
import { Form, Space } from 'antd';
import {
  ApprovalsConfigsDrawerForm,
  FormValues,
} from './_approvals-configs-drawer-form';
import { isEqual } from 'lodash';
import { createMessage } from 'works/legacy/lib/message-helper';
import { useMutation, useQuery } from '@common/hooks';
import { UpsertApprovalsConfigMutation } from '@approvals/graphql/mutations';
import { ApprovalsConfigQuery } from '@approvals/graphql/queries';

type Props = {
  open: boolean;
  onClose: () => void;
};

export const ApprovalsConfigsDrawer = ({ open, onClose }: Props) => {
  const [form] = Form.useForm();
  const [anyChanges, setAnyChanges] = useState(false);

  const { loading, error, data } = useQuery(ApprovalsConfigQuery);

  const [upsertConfigs, { loading: loadingUpsert }] = useMutation(
    UpsertApprovalsConfigMutation,
    {
      onCompleted: () => {
        createMessage('success', 'Approvals options saved successfully');
        onClose();
      },
      onError: () => {
        createMessage('error', 'An error occurred. Please try again.');
      },
    }
  );

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ApprovalsConfigDrawer"
        messageType="contextual"
      />
    );
  }

  const initialValues: FormValues | undefined = data && {
    requireStaffingRequestDocument:
      data.approvalConfigs?.requireStaffingRequestDocument || false,
    staffingRequestDocumentNote:
      data.approvalConfigs?.staffingRequestDocumentNote || '',
    requireExtensionOfferDocument:
      data.approvalConfigs?.requireExtensionOfferDocument || false,
    extensionOfferDocumentNote:
      data.approvalConfigs?.extensionOfferDocumentNote || '',
  };

  const onFormChange = () => {
    const changed = !isEqual(form.getFieldsValue(true), initialValues);
    setAnyChanges(changed);
  };

  const onFormFinish = () => {
    const formValues = form.getFieldsValue();

    upsertConfigs({
      variables: {
        input: formValues,
      },
    });
  };

  const footer = (
    <ButtonGroup>
      <SecondaryButton size="large" onClick={onClose}>
        Cancel
      </SecondaryButton>
      <PrimaryButton
        size="large"
        onClick={form.submit}
        disabled={!anyChanges}
        loading={loadingUpsert}
      >
        Save
      </PrimaryButton>
    </ButtonGroup>
  );

  return (
    <Drawer
      className="approvals-configs-drawer"
      title="Approvals Options"
      open={open}
      onClose={onClose}
      footer={footer}
    >
      <Space direction="vertical" size={16}>
        {loading || !initialValues ? (
          <LoadingState />
        ) : (
          <>
            <Banner
              type="info"
              content="Options are applied to all facilities, but only at units that have an active approval chain set."
            />
            <Text>Documents Requirements</Text>
            <ApprovalsConfigsDrawerForm
              form={form}
              initialValues={initialValues}
              onFormFinish={onFormFinish}
              onFormChange={onFormChange}
            />
          </>
        )}
      </Space>
    </Drawer>
  );
};
